import React, { useEffect, useState } from "react";
import { getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { getIdCultureUser } from "@/main/services/utilsUser";
import { useForm } from "react-hook-form";
import { isAfter, isBefore, isWithinInterval, parse, setYear } from "date-fns";
import lodash from "lodash";
import { companyBusinessHourApi } from "@/main/api";
import { DayPeriodEnum } from "@/main/services/enum/DayPeriod";
import { BusinessHourResponse } from "@/main/models/response";

import * as S from "./styles";
import KogInputClockMask from "../inputMask/inputClockMask";
import KogInputTextControlledNew from "../inputText/inputTextControlledNew";
import SecondaryButton from "../button/secondaryButton";
import KogAutoCompleteControlled from "../inputAutoComplete/autoCompleteControlled";
import { toast } from "react-toastify";
import { formatDateToISOString, formatHours } from "@/main/services/utilsDate";
import useAsyncModalConfirmReason from "./../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

export const defaultValuePeriodBusiness = () => {
	const currentDate = new Date();
	const initialHour = currentDate.setHours(8, 0, 0, 0);
	const finalHour = currentDate.setHours(18, 0, 0, 0);

	return [
		{
			id: String(currentDate.getTime()),
			initialHour: new Date(initialHour),
			finalHour: new Date(finalHour),
			description: getTranslateByKey("information.workingHours"),
		},
	];
};

export interface IPeriodBusinessInfoState {
	id?: string;
	guid?: string;
	initialHour: Date;
	finalHour: Date;
	description: string;
	guidInsurer?: string;
	guidBodyShop?: string;
}

interface IPeriodBusinessInfoForm {
	initialHour: string;
	initialHourType: string;
	finalHour: string;
	finalHourType: string;
	description: string;
	id?: string;
	guid?: string;
}
const INITIAL_FORM_STATE: IPeriodBusinessInfoForm = {
	initialHour: "",
	initialHourType: DayPeriodEnum.Am,
	finalHour: "",
	finalHourType: DayPeriodEnum.Pm,
	description: "",
	id: "",
};

interface IBusinessHoursModal {
	guidInsurer?: string | null;
	guidBodyShop?: string | null;
	hasEdit: boolean;
	setHasEdit: React.Dispatch<React.SetStateAction<boolean>>;
	businessHourEdit: IPeriodBusinessInfoState | null;
	dataPeriodBusiness: BusinessHourResponse[];
	setDataPeriodBusiness: React.Dispatch<React.SetStateAction<BusinessHourResponse[]>>;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function BusinessHoursModal({
	guidInsurer = null,
	guidBodyShop = null,
	hasEdit,
	setHasEdit,
	businessHourEdit,
	dataPeriodBusiness,
	setDataPeriodBusiness,
	setOpen,
}: IBusinessHoursModal) {
	const [typeInitialHourDefault, setTypeInitialHourDefault] = useState<string>("am");
	const [typeFinalHourDefault, setTypeFinalHourDefault] = useState<string>("pm");
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const mockedHours = [
		{ value: "AM", key: DayPeriodEnum.Am },
		{ value: "PM", key: DayPeriodEnum.Pm },
	];

	function resetValueFormAndTypeHour() {
		setValue("id", "");
		setValue("initialHour", "");
		setValue("finalHour", "");
		setValue("description", "");
		setTypeInitialHourDefault(DayPeriodEnum.Am);
		setTypeFinalHourDefault(DayPeriodEnum.Pm);
	}
	useEffect(() => {
		if (hasEdit && businessHourEdit) {
			setHasEdit(true);
			const arrInitialHour = formatHours(businessHourEdit.initialHour, getIdCultureUser()).split(" ");
			const arrFinalHour = formatHours(businessHourEdit.finalHour, getIdCultureUser()).split(" ");
			const initialHour = arrInitialHour[0];
			const finalHour = arrFinalHour[0];
			const typeInitialHour = arrInitialHour[1];
			const typeFinalHour = arrFinalHour[1];

			setValue("id", businessHourEdit.id);
			setValue("guid", businessHourEdit.guid);
			setValue("initialHour", initialHour);
			setValue("finalHour", finalHour);
			setValue("description", businessHourEdit.description);
			clearErrors();
			setTypeInitialHourDefault(String(typeInitialHour.toLowerCase()));
			setTypeFinalHourDefault(String(typeFinalHour.toLowerCase()));
		} else {
			resetValueFormAndTypeHour();
		}
	}, [hasEdit, businessHourEdit]);

	const formatDataPeriodBusiness = (periodBusiness: IPeriodBusinessInfoState[], isEditPeriodBusiness = false) => {
		return removeEmptyValues({
			...(isEditPeriodBusiness && {
				guid: periodBusiness.guid,
			}),
			guidInsurer,
			guidBodyShop,
			startDate: formatDateToISOString(periodBusiness.initialHour),
			endDate: formatDateToISOString(periodBusiness.finalHour),
			description: periodBusiness.description,
			idCompanyBusinessHour: 0,
		});
	};

	const {
		setValue,
		handleSubmit,
		clearErrors,
		setError,
		control,
		formState: { errors },
	} = useForm<IPeriodBusinessInfoForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE,
	});

	const validationHour = (initialHourValidate: Date, finalHourValidate: Date, indexEdit: number) => {
		if (
			initialHourValidate.getHours() === finalHourValidate.getHours() &&
			initialHourValidate.getTime() === finalHourValidate.getTime()
		) {
			setError("initialHour", {
				type: "manual",
				message: getTranslateByKey("businessHour.theHoursMustNotBeTheSame", true),
			});
			setError("finalHour", {
				type: "manual",
				message: getTranslateByKey("businessHour.theHoursMustNotBeTheSame", true),
			});
			return false;
		}
		const baseData =
			dataPeriodBusiness.length > 0
				? dataPeriodBusiness
				: [
						{
							id: "",
							initialHour: new Date(),
							finalHour: new Date(),
							description: "",
						},
					];
		const firstDate = baseData[0]?.initialHour;
		const clonedDataPeriodBusiness = lodash.cloneDeep(dataPeriodBusiness);

		if (indexEdit !== null) {
			clonedDataPeriodBusiness.splice(indexEdit, 1);
		}
		const adjustedStartDate = setYear(initialHourValidate, firstDate.getFullYear());
		const adjustedEndDate = setYear(finalHourValidate, firstDate.getFullYear());

		if (isNaN(initialHourValidate.getHours()) || isNaN(finalHourValidate.getHours())) {
			setError("initialHour", {
				type: "manual",
				message: getTranslateByKey("information.hoursMustBeBetween01And12", true),
			});
			setError("finalHour", {
				type: "manual",
				message: getTranslateByKey("information.hoursMustBeBetween01And12", true),
			});
		}

		if (isAfter(initialHourValidate, finalHourValidate)) {
			setError("initialHour", {
				type: "manual",
				message: getTranslateByKey("information.initialHourMustBeLowerThanFinalHour", true),
			});
			setError("finalHour", {
				type: "manual",
				message: getTranslateByKey("information.initialHourMustBeLowerThanFinalHour", true),
			});
		}

		const isWithinRange = clonedDataPeriodBusiness.some(
			(item) =>
				(isBefore(adjustedStartDate, item.finalHour) &&
					isWithinInterval(adjustedStartDate, {
						start: item.initialHour,
						end: item.finalHour,
					})) ||
				(isBefore(adjustedEndDate, item.finalHour) &&
					isWithinInterval(adjustedEndDate, {
						start: item.initialHour,
						end: item.finalHour,
					})),
		);

		const isWithinRange2 = clonedDataPeriodBusiness.some(
			(item) =>
				isWithinInterval(item.initialHour, {
					start: adjustedStartDate,
					end: adjustedEndDate,
				}) ||
				isWithinInterval(item.finalHour, {
					start: adjustedStartDate,
					end: adjustedEndDate,
				}),
		);

		const isWithinRange3 =
			clonedDataPeriodBusiness.some(
				(item) =>
					item.initialHour.getTime() === adjustedStartDate.getTime() ||
					item.finalHour.getTime() === adjustedEndDate.getTime(),
			) || adjustedStartDate.getTime() === adjustedEndDate.getTime();

		if (isWithinRange || isWithinRange2 || isWithinRange3) {
			setError("initialHour", {
				type: "manual",
				message: getTranslateByKey("information.hoursMustNotBeWithinRangeOfAnotherPeriod", true),
			});
			setError("finalHour", {
				type: "manual",
				message: getTranslateByKey("information.hoursMustNotBeWithinRangeOfAnotherPeriod", true),
			});
			return false;
		} else {
			clearErrors(["initialHour", "finalHour"]);
			return true;
		}
	};

	const convertStringToHour = (hour: string, type: string) => {
		let hourString = `${hour} ${type}`;

		if (hour.startsWith("00") && type.toLowerCase() === "am") {
			hourString = hourString.replace(/^00/, "12");
		}
		const convertedDate = parse(hourString, "hh:mm a", new Date());
		return convertedDate;
	};

	async function handleSubmitBusinessHoursInfo(data: IPeriodBusinessInfoForm) {
		setLoadingSave(true);

		try {
			const convertedInitialhour = convertStringToHour(data.initialHour, data?.initialHourType?.key);
			const convertedFinalHour = convertStringToHour(data.finalHour, data?.finalHourType?.key);

			const indexEdit = dataPeriodBusiness.findIndex((item) => item.id === data.id);
			if (!validationHour(convertedInitialhour, convertedFinalHour, indexEdit)) return;

			const intitialHourFormatted = new Date();
			intitialHourFormatted.setHours(convertedInitialhour.getHours(), convertedInitialhour.getMinutes());

			const finalHourFormatted = new Date();
			finalHourFormatted.setHours(convertedFinalHour.getHours(), convertedFinalHour.getMinutes());

			const newData = {
				initialHour: intitialHourFormatted,
				finalHour: finalHourFormatted,
				description: data.description,
			} as IPeriodBusinessInfoState;

			const formattedPeriodBusiness: BusinessHourRequest = formatDataPeriodBusiness(newData) as BusinessHourRequest;

			let filesRequest = undefined;
			let guidReason = null;
			let reasonDescription = null;

			if (guidInsurer || guidBodyShop) {
				const resultConfirm = await getConfirmationReason(
					getTranslateByKey("information.confirmChangeTitle"),
					ReasonTypeEnum.ChangeCompany,
				);

				if (!resultConfirm.confirmed) {
					setLoadingSave(false);
					return;
				}

				if (resultConfirm.files && resultConfirm.files.length > 0) {
					filesRequest = await Promise.all(
						resultConfirm.files.map(async (item) => {
							const base64DataUrl = await toBase64(item);
							return {
								fileName: item.name,
								contentType: item.type,
								fileBase64: base64DataUrl as string,
							};
						}),
					);
				}

				guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
				reasonDescription = resultConfirm.observation || null;
			}

			formattedPeriodBusiness.changeJustification = {
				guidReason: guidReason,
				reasonDescription: reasonDescription,
				filesUpload: filesRequest,
			};

			companyBusinessHourApi.saveBusinessHour(formattedPeriodBusiness).then((res) => {
				const resData = res.data.result;
				setDataPeriodBusiness((prevState) => {
					return [...prevState, { id: resData.guid, ...newData }];
				});
				toast.success(getTranslateByKey("businessHour.businessHourSavedSuccessfully"));
				setOpen(false);
				resetValueFormAndTypeHour();
			});
		} catch {
		} finally {
			setLoadingSave(false);
		}
	}

	async function handleSubmitEditBusinessHoursInfo(data: IPeriodBusinessInfoForm) {
		setLoadingSave(true);
		try {
			const index = dataPeriodBusiness.findIndex((item) => item.id === data.id);

			const convertedInitialhour = convertStringToHour(data.initialHour, data?.initialHourType?.key);
			const convertedFinalHour = convertStringToHour(data.finalHour, data?.finalHourType?.key);

			if (!validationHour(convertedInitialhour, convertedFinalHour)) return;

			const intitialHourFormatted = new Date();
			intitialHourFormatted.setHours(convertedInitialhour.getHours(), convertedInitialhour.getMinutes());

			const finalHourFormatted = new Date();
			finalHourFormatted.setHours(convertedFinalHour.getHours(), convertedFinalHour.getMinutes());

			const newData = {
				id: data.id,
				guid: data.guid,
				initialHour: intitialHourFormatted,
				finalHour: finalHourFormatted,
				description: data.description,
			} as IPeriodBusinessInfoState;

			const formattedPeriodBusiness = formatDataPeriodBusiness(newData, true) as BusinessHourRequest;

			let filesRequest = undefined;
			let guidReason = null;
			let reasonDescription = null;

			if (guidInsurer || guidBodyShop) {
				const resultConfirm = await getConfirmationReason(
					getTranslateByKey("information.confirmChangeTitle"),
					ReasonTypeEnum.ChangeCompany,
				);

				if (!resultConfirm.confirmed) {
					setLoadingSave(false);
					return;
				}

				if (resultConfirm.files && resultConfirm.files.length > 0) {
					filesRequest = await Promise.all(
						resultConfirm.files.map(async (item) => {
							const base64DataUrl = await toBase64(item);
							return {
								fileName: item.name,
								contentType: item.type,
								fileBase64: base64DataUrl as string,
							};
						}),
					);
				}

				guidReason = resultConfirm.reason ? String(resultConfirm.reason.key) : null;
				reasonDescription = resultConfirm.observation || null;
			}

			formattedPeriodBusiness.changeJustification = {
				guidReason: guidReason,
				reasonDescription: reasonDescription,
				filesUpload: filesRequest,
			};

			companyBusinessHourApi.saveBusinessHour(formattedPeriodBusiness).then(() => {
				setDataPeriodBusiness((prevState) => {
					const clonePrevState = lodash.cloneDeep(prevState);
					clonePrevState[index] = {
						...data,
						id: data.id,
						guid: data.guid,
						initialHour: intitialHourFormatted,
						finalHour: finalHourFormatted,
					} as IPeriodBusinessInfoState;

					return [...clonePrevState];
				});
				toast.success(getTranslateByKey("businessHour.businessHourSavedSuccessfully"));
				resetValueFormAndTypeHour();
				setHasEdit(false);
				setOpen(false);
			});
		} catch {
		} finally {
			setLoadingSave(false);
		}
	}

	return (
		<>
			<S.HeaderText>{getTranslateByKey("information.periodOfBusinessHours")}</S.HeaderText>
			<form
				onSubmit={handleSubmit(hasEdit ? handleSubmitEditBusinessHoursInfo : handleSubmitBusinessHoursInfo)}
				noValidate
			>
				<S.AreaForm>
					<KogInputClockMask
						name="initialHour"
						label={getTranslateByKey("information.initialHour")}
						control={control}
						controllerProps={{ rules: { required: true, minLength: 5 } }}
						errorMessageCustom={
							errors.initialHour?.type === "required" ? getTranslateByKey("information.initialHourRequired", true) : ""
						}
						id="id-register-text-initialHour"
					/>
					<KogAutoCompleteControlled
						nameCustom="initialHourType"
						label={getTranslateByKey("businessHour.initialHourPeriod")}
						control={control}
						setValueCustom={setValue}
						options={mockedHours}
						rulesCustom={{ required: true }}
						onChangeCustom={() => {}}
						valueDefaultKey={typeInitialHourDefault}
						id="id-register-text-initialHourType"
					/>
					<KogInputClockMask
						name="finalHour"
						label={getTranslateByKey("information.finalHour")}
						control={control}
						controllerProps={{ rules: { required: true, minLength: 5 } }}
						errorMessageCustom={
							errors.finalHour?.type === "required" ? getTranslateByKey("information.finalHourRequired", true) : ""
						}
						id="id-businessHours-text-finalHour"
					/>
					<KogAutoCompleteControlled
						id="id-register-text-finalHourType"
						nameCustom="finalHourType"
						label={getTranslateByKey("businessHour.finalHourPeriod")}
						control={control}
						setValueCustom={setValue}
						options={mockedHours}
						rulesCustom={{ required: true }}
						onChangeCustom={() => {}}
						valueDefaultKey={typeFinalHourDefault}
					/>
					<KogInputTextControlledNew
						name="description"
						label={getTranslateByKey("information.description")}
						control={control}
						inputProps={{ maxLength: 50 }}
						controllerProps={{ rules: { required: true } }}
						errorMessageCustom={
							errors.description?.type === "required" ? getTranslateByKey("information.descriptionRequired", true) : ""
						}
						id="id-businessHours-text-description"
					/>
				</S.AreaForm>
				<S.AreaButton>
					<SecondaryButton id="id-businessHours-button-save" type="submit" isLoading={loadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</S.AreaButton>
			</form>
			<ModalConfirmationReason />
		</>
	);
}
export default BusinessHoursModal;
